import React, { useState, useMemo } from 'react';
import { MdArrowBack, MdChevronRight, MdSearch, MdCheckCircle } from 'react-icons/md';
import accountsData from '../data/accounts.json';
import { useProfile } from '../contexts/ProfileContext';

const PAYEES = [
  { id: 'rogers',    name: 'Rogers',            category: 'Telecom',   logo: '📱' },
  { id: 'bell',      name: 'Bell Canada',        category: 'Telecom',   logo: '📡' },
  { id: 'telus',     name: 'TELUS',              category: 'Telecom',   logo: '📶' },
  { id: 'hydro-one', name: 'Hydro One',          category: 'Utilities', logo: '⚡' },
  { id: 'enbridge',  name: 'Enbridge Gas',       category: 'Utilities', logo: '🔥' },
  { id: 'city-water',name: 'City Water',         category: 'Utilities', logo: '💧' },
  { id: 'netflix',   name: 'Netflix',            category: 'Streaming', logo: '🎬' },
  { id: 'spotify',   name: 'Spotify',            category: 'Streaming', logo: '🎵' },
  { id: 'amazon',    name: 'Amazon Prime',       category: 'Streaming', logo: '📦' },
  { id: 'insurance', name: 'Sun Life Insurance', category: 'Insurance', logo: '🛡️' },
  { id: 'td-loan',   name: 'TD Personal Loan',   category: 'TD Bank',   logo: '🏦' },
  { id: 'td-visa',   name: 'TD VISA',            category: 'TD Bank',   logo: '💳' },
  { id: 'cra',       name: 'CRA (Tax)',          category: 'Government',logo: '🇨🇦' },
  { id: 'condo',     name: 'Condo Fees',         category: 'Housing',   logo: '🏢' },
  { id: 'rent',      name: 'Rent / Landlord',    category: 'Housing',   logo: '🏠' },
];

const bankingAccounts = accountsData.bankingData.filter(a => a.currency === 'CAD');

function getAccounts(profile) {
  if (!profile?.accounts) return bankingAccounts;
  const fmt = v => `$${Number(Math.abs(v || 0)).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const accts = [];
  const acctBal = (a) => a?.current_balance ?? a?.current_balance_owing ?? a?.current_value ?? a?.current_market_value ?? a?.balance;
  if (profile.accounts.chequing) accts.push({ id: 'chequing', name: profile.accounts.chequing.name || 'Chequing Account', num: '...' + String(profile.accounts.chequing.account_number || '').slice(-4), balance: fmt(acctBal(profile.accounts.chequing)) });
  if (profile.accounts.savings)  accts.push({ id: 'savings',  name: profile.accounts.savings.name  || 'Savings Account',  num: '...' + String(profile.accounts.savings.account_number  || '').slice(-4), balance: fmt(acctBal(profile.accounts.savings)) });
  return accts.length ? accts : bankingAccounts;
}

const today = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });

const BillPaymentScreen = ({ onNavigate, onBack }) => {
  const { loadedProfileData } = useProfile();
  const accounts = getAccounts(loadedProfileData?.profile);

  const [step, setStep]           = useState('select'); // 'select' | 'amount' | 'review' | 'success'
  const [search, setSearch]       = useState('');
  const [payee, setPayee]         = useState(null);
  const [fromAccount, setFrom]    = useState(accounts[0]?.id || 'chequing');
  const [amount, setAmount]       = useState('');
  const [errors, setErrors]       = useState({});

  const selectedAccount = accounts.find(a => a.id === fromAccount) || accounts[0];

  const filtered = useMemo(() => {
    if (!search.trim()) return PAYEES;
    const q = search.toLowerCase();
    return PAYEES.filter(p => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
  }, [search]);

  const grouped = useMemo(() => {
    return filtered.reduce((acc, p) => {
      if (!acc[p.category]) acc[p.category] = [];
      acc[p.category].push(p);
      return acc;
    }, {});
  }, [filtered]);

  const handleSelectPayee = (p) => { setPayee(p); setStep('amount'); };

  const validateAmount = () => {
    const amt = parseFloat(amount);
    if (!amount || isNaN(amt) || amt <= 0) { setErrors({ amount: 'Enter a valid amount' }); return false; }
    if (amt > 99999)                        { setErrors({ amount: 'Amount exceeds limit' }); return false; }
    setErrors({});
    return true;
  };

  const fmt = v => `$${parseFloat(v || 0).toFixed(2)}`;

  if (step === 'success') return (
    <div className="screen bp-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <h1 className="bh-flow-title">Pay Bills</h1>
        </div>
      </header>
      <div className="emt-success-body">
        <div className="emt-success-circle">
          <MdCheckCircle size={52} color="var(--td-green)" />
        </div>
        <div className="emt-success-title">Payment Scheduled!</div>
        <div className="emt-success-amount">{fmt(amount)}</div>
        <div className="emt-success-sub">paid to {payee?.name}</div>
        <div className="emt-success-eta">Processing date: {today}</div>
        <button className="emt-primary-btn" style={{ marginTop: 32 }} onClick={() => onNavigate('home')}>Done</button>
        <button className="emt-ghost-btn" onClick={() => { setStep('select'); setPayee(null); setAmount(''); }}>Pay Another Bill</button>
      </div>
    </div>
  );

  if (step === 'review') return (
    <div className="screen bp-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={() => setStep('amount')} aria-label="Back">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Review Payment</h1>
        </div>
      </header>
      <div className="scroll-content gray-bg">
        <div className="emt-review-amount">{fmt(amount)}</div>
        <div className="emt-section">
          <div className="emt-review-row">
            <span className="emt-review-label">Payee</span>
            <span className="emt-review-value">{payee?.logo} {payee?.name}</span>
          </div>
          <div className="emt-review-row">
            <span className="emt-review-label">From</span>
            <span className="emt-review-value">{selectedAccount?.name}<br /><span style={{ fontSize: 11, color: 'var(--text-light)' }}>{selectedAccount?.num} · {selectedAccount?.balance}</span></span>
          </div>
          <div className="emt-review-row">
            <span className="emt-review-label">Amount</span>
            <span className="emt-review-value">{fmt(amount)} CAD</span>
          </div>
          <div className="emt-review-row">
            <span className="emt-review-label">Date</span>
            <span className="emt-review-value">{today}</span>
          </div>
        </div>
        <div className="emt-notice">
          Payment will be processed today. It may take 1–2 business days to appear on your payee's account.
        </div>
        <button className="emt-primary-btn" onClick={() => setStep('success')}>Confirm Payment</button>
        <button className="emt-ghost-btn" onClick={() => setStep('amount')}>Edit</button>
      </div>
    </div>
  );

  if (step === 'amount') return (
    <div className="screen bp-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={() => setStep('select')} aria-label="Back">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Pay Bills</h1>
        </div>
      </header>
      <div className="scroll-content gray-bg">

        {/* Selected payee banner */}
        <div className="bp-payee-banner">
          <span className="bp-payee-logo">{payee?.logo}</span>
          <div>
            <div className="bp-payee-banner-name">{payee?.name}</div>
            <div className="bp-payee-banner-cat">{payee?.category}</div>
          </div>
          <button className="bp-change-btn" onClick={() => setStep('select')}>Change</button>
        </div>

        {/* From account */}
        <div className="emt-section">
          <div className="emt-section-title">From Account</div>
          {accounts.map(a => (
            <div key={a.id} className={`emt-account-row${fromAccount === a.id ? ' selected' : ''}`} onClick={() => setFrom(a.id)}>
              <div className="emt-account-info">
                <div className="emt-account-name">{a.name}</div>
                <div className="emt-account-num">{a.num}</div>
              </div>
              <div className="emt-account-right">
                <div className="emt-account-balance">{a.balance}</div>
                <div className={`emt-radio${fromAccount === a.id ? ' on' : ''}`} />
              </div>
            </div>
          ))}
        </div>

        {/* Amount */}
        <div className="emt-section">
          <div className="emt-section-title">Payment Amount</div>
          <div className={`emt-amount-wrapper${errors.amount ? ' error' : ''}`}>
            <span className="emt-currency-symbol">$</span>
            <input
              className="emt-amount-input"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={e => { setAmount(e.target.value); setErrors({}); }}
            />
            <span className="emt-currency-code">CAD</span>
          </div>
          {errors.amount && <div className="emt-error" style={{ marginTop: 6 }}>{errors.amount}</div>}
        </div>

        {/* Payment date */}
        <div className="emt-section">
          <div className="emt-section-title">Payment Date</div>
          <div className="bp-date-row">
            <span className="bp-date-label">Today</span>
            <span className="bp-date-value">{today}</span>
          </div>
        </div>

        <button className="emt-primary-btn" onClick={() => { if (validateAmount()) setStep('review'); }}>Review Payment</button>
      </div>
    </div>
  );

  // step === 'select'
  return (
    <div className="screen bp-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back to home">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Pay Bills</h1>
        </div>
      </header>

      {/* Search */}
      <div className="bp-search-bar">
        <MdSearch size={18} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
        <input
          className="bp-search-input"
          placeholder="Search payees…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="scroll-content gray-bg">
        {Object.keys(grouped).length === 0 && (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            No payees found for "{search}"
          </div>
        )}
        {Object.entries(grouped).map(([category, items]) => (
          <div key={category}>
            <div className="list-group-title">{category}</div>
            {items.map(p => (
              <div key={p.id} className="bp-payee-row" onClick={() => handleSelectPayee(p)}>
                <span className="bp-payee-logo-sm">{p.logo}</span>
                <span className="bp-payee-row-name">{p.name}</span>
                <MdChevronRight style={{ color: 'var(--text-light)', flexShrink: 0 }} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BillPaymentScreen;
